import { toolsBase } from './tools-base';
import { format } from 'date-fns';
import { BASE_TITLE, parseQueryParams, navigateTo} from "../router/routes";
import { values } from "./values"
import { isPublicRoute } from "../config/routes.js";
import { getAuthService } from './service-loader.js';

// Promise to track ongoing API request (prevents duplicate concurrent calls)
let userContextPromise = null;

// Cache for lazy-loaded store to avoid repeated imports
let storeCache = null;

// Helper to get store at runtime (lazy import to break circular dependency)
async function getStore() {
    if (!storeCache) {
        const module = await import('./store');
        storeCache = module.store;
    }
    return storeCache;
}

export const tools = {
    // Inherit all base utilities
    ...toolsBase,

    debounce: toolsBase.debounce,
    encodeObjectToBase64: toolsBase.encodeObjectToBase64,
    decodeBase64ToObject: toolsBase.decodeBase64ToObject,
    getGreeting: toolsBase.getGreeting,
    mediaTypeDisplay: toolsBase.mediaTypeDisplay,
    authoringToolDisplay: toolsBase.authoringToolDisplay,
    languageDisplay: toolsBase.languageDisplay,
    searchTypeDisplay: toolsBase.searchTypeDisplay,
    fileSizeDisplay: toolsBase.fileSizeDisplay,
    generateUUID: toolsBase.generateUUID,
    getUserLocale: toolsBase.getUserLocale,
    formatDate: toolsBase.formatDate,
    formatDateTime: toolsBase.formatDateTime,
    formatTime: toolsBase.formatTime,
    downloadBlob: toolsBase.downloadBlob,
    downloadFile: toolsBase.downloadFile,
    getItemStatusColor: toolsBase.getItemStatusColor,
    resourceStatusDisplay: toolsBase.resourceStatusDisplay,
    resourceActionDisplay: toolsBase.resourceActionDisplay,
    skillOriginDisplay: toolsBase.skillOriginDisplay,
    isValidFileType: toolsBase.isValidFileType,
    isValidJson: toolsBase.isValidJson,
    isValidJsonString: toolsBase.isValidJsonString,
    formatExtractChunk: toolsBase.formatExtractChunk,
    highlightKeywords: toolsBase.highlightKeywords,
    formatExtractChunkScore: toolsBase.formatExtractChunkScore,
    sortCollectionsByName: toolsBase.sortCollectionsByName,
    formatNumber: toolsBase.formatNumber,
    formatCurrency: toolsBase.formatCurrency,
    getResourceAttribute: toolsBase.getResourceAttribute,

    getResourceNameColor(resource) {
        return resource?.mostRecentJobStatus === 'failed' ? 'text-red-900' : '';
    },

    getQueryParams() {
        const hash = window.location.hash
        return parseQueryParams(hash);
    },

    navigateTo(hash) {
        navigateTo(hash);
    },

    goBack(fallback = "home") {
        // Flag that we're doing a programmatic back navigation
        // This allows components to detect both browser back and programmatic back
        try {
            sessionStorage.setItem('programmatic-back', 'true');
        } catch (error) {
            console.warn('Failed to set programmatic-back flag:', error);
        }

        // Route = hash with the query string stripped. Pages that switch
        // sub-tabs/modes in place (e.g. summary.tag's setActiveTab,
        // content.tag's setMode) push one hashHistory entry per switch even
        // though the route never changes - only the `?tab=`/`?mode=` param
        // does. Popping a fixed number of entries would land back on one of
        // those in-page tab states instead of the page the user actually
        // came from. Instead, walk back until we find an entry whose route
        // differs from the current one.
        const getRoute = (hash) => (hash || '').split('?')[0];
        const currentRoute = getRoute(window.location.hash);

        let target = null;
        while (window.hashHistory.length > 0) {
            const candidate = window.hashHistory.pop();
            if (getRoute(candidate) !== currentRoute) {
                target = candidate;
                break;
            }
        }

        if (target) {
            window.location.hash = target;
        } else {
            navigateTo(fallback);
        }
    },

    /**
     * Check if user arrived via back/forward browser button or programmatic goBack()
     * Checks session storage flags set by popstate listener (browser back) and goBack() (programmatic back)
     * @returns {boolean} True if navigation was via back/forward button or tools.goBack()
     */
    isBackNavigation() {
        try {
            // Check for browser back/forward button (set by popstate listener in index.tag)
            const isBrowserBack = sessionStorage.getItem('browser-back-forward') === 'true';
            if (isBrowserBack) {
                sessionStorage.removeItem('browser-back-forward');
                console.log('[Tools] Detected browser back/forward navigation');
                return true;
            }

            // Check for programmatic back navigation (via tools.goBack())
            const isProgrammaticBack = sessionStorage.getItem('programmatic-back') === 'true';
            if (isProgrammaticBack) {
                sessionStorage.removeItem('programmatic-back');
                console.log('[Tools] Detected programmatic back navigation');
                return true;
            }
        } catch (error) {
            console.warn('[Tools] Failed to check back navigation flags:', error);
        }

        // No back navigation detected
        return false;
    },

    /**
     * Clear back navigation flags from sessionStorage
     * Call this when performing explicit forward navigation (e.g., clicking nav items)
     * to ensure the next page load is not mistakenly detected as back navigation
     */
    clearBackNavigationFlags() {
        try {
            sessionStorage.removeItem('browser-back-forward');
            sessionStorage.removeItem('programmatic-back');
        } catch (error) {
            console.warn('[Tools] Failed to clear navigation flags:', error);
        }
    },

    /**
     * Clear all page-specific table state from sessionStorage
     * Iterates through sessionStorage and removes keys ending with '-state'
     * Call this during explicit forward navigation to ensure fresh page loads
     */
    clearAllPageStates() {
        try {
            const keysToRemove = [];

            // Iterate through sessionStorage to find state keys
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                // Clear any keys ending with '-state' (our page state convention)
                if (key && key.endsWith('-state')) {
                    keysToRemove.push(key);
                }
            }

            // Remove the keys (do this after iteration to avoid index issues)
            keysToRemove.forEach(key => sessionStorage.removeItem(key));

            if (keysToRemove.length > 0) {
                console.log('[Tools] Cleared page states:', keysToRemove);
            }
        } catch (error) {
            console.warn('[Tools] Failed to clear page states:', error);
        }
    },

    async getSliceId() {
        const store = await getStore();
        return store.getState().sliceId;
    },

    async replacePageTitle(title) {
        if (!title) return;

        const store = await getStore()
        const state = store.getState()
        const customerName = state.customer?.name

        // Build title parts
        const titleParts = [title]

        // Add customer name if available (helps distinguish tabs)
        if (customerName) {
            titleParts.push(customerName)
        }

        // Always end with base title
        titleParts.push(BASE_TITLE)

        document.title = titleParts.join(' - ')
    },

    async fetchAndUpdateQuota() {
        try {
            const { quotaService } = await import('../api/quotaService');
            const quotaDetails = await quotaService.getDetails(null, true);
            const store = await getStore();
            store.dispatch({ type: 'setQuotaDetails', payload: quotaDetails });
            return quotaDetails;
        } catch (error) {
            console.error('[Tools] Failed to fetch quota details:', error);
            return null;
        }
    },

    // Single source of truth for /billing/subscription. Subscription-banner
    // (mounted in layout on every page) and upload-page both read the result
    // from the store, so a single fetch on app boot or 4-hour refresh covers
    // both consumers.
    async fetchAndUpdateSubscriptionStatus() {
        try {
            const { billingService } = await import('../api/billingService');
            const status = await billingService.getSubscriptionStatus(true);
            const store = await getStore();
            store.dispatch({ type: 'setSubscriptionStatus', payload: status });
            return status;
        } catch (error) {
            console.error('[Tools] Failed to fetch subscription status:', error);
            // Preserve the 402 → "Subscription Expired" banner path that used
            // to live inside subscription-banner.checkSubscriptionStatus.
            if (error?.response?.status === 402) {
                const store = await getStore();
                store.dispatch({
                    type: 'setSubscriptionStatus',
                    payload: { status: 'expired', canUseCredits: false, canUploadResources: false }
                });
            }
            return null;
        }
    },

    async isQuotaExhausted() {
        const store = await getStore();
        const quotaDetails = store.getState().quotaDetails;

        if (!quotaDetails) {
            // If quota not loaded yet, assume not exhausted
            return false;
        }

        return quotaDetails.remainingCredits <= 0 || quotaDetails.usedCredits >= quotaDetails.totalCredits;
    },

    /**
     * Check if an error message indicates a quota/credits error
     * @param {string} message - Error message to check
     * @returns {boolean} True if message contains quota/credits error
     */
    isQuotaError(message) {
        if (!message) return false;
        const lowerMessage = String(message).toLowerCase();
        return lowerMessage.includes("insufficient quota credits") ||
               lowerMessage.includes("insufficient processing credits");
    },

    async uploadFile(file, resourceId = null, collectionId = null, metadata = null, skipLoading = false) {
        // Lazy import only when needed
        const { resourceService } = await import('../api/resourceService');
        const store = await getStore();

        if (!resourceId) resourceId = this.generateUUID();
        const jobId = this.generateUUID();

        const uploadParams = new URLSearchParams({
            filename: file.name,
            resourceId: resourceId,
            jobId: jobId,
            useSuggestions: true
        });

        if (collectionId) {
            uploadParams.set('collectionId', collectionId);
        }

        if (metadata) {
            if (metadata.title) uploadParams.set('displayName', metadata.title);
            if (metadata.description) uploadParams.set('description', metadata.description);
            if (metadata.externalSource) uploadParams.set('externalSourceName', metadata.externalSource);
            if (metadata.externalId) uploadParams.set('externalSourceId', metadata.externalId);
            if (metadata.title || metadata.description) {
                uploadParams.set('useSuggestions', false);
            }
        }

        if (import.meta.env.MODE === 'development') {
            if (!skipLoading) store.dispatch({ type: 'incrementRequests'});
            await new Promise(resolve => setTimeout(resolve, Math.random() * 10000));
            if (!skipLoading) store.dispatch({ type: 'decrementRequests'});
            return { success: true, resourceId };
        } else {
            try {
                const uploadData = await resourceService.provision(uploadParams, skipLoading);
                const s3Response = await resourceService.upload(uploadData.uploadUrl, file, skipLoading);
                return { success: true, resourceId, response: s3Response };
            } catch (err) {
                const message =
                    err?.response?.data?.message ||  // Axios-style server message
                    err?.message ||                  // generic JS error
                    String(err);

                if (this.isQuotaError(message)) {
                    this.showQuotaLimitModal();
                }
                throw err;
            }

        }

    },

    async uploadFiles(files = [], completeMessage = null, collectionId = null) {
        // Lazy import only when needed
        const { modalService } = await import('./modalService');
        const store = await getStore();

        let maxFiles = values.maxFiles;

        let uploadedFileNames = [];
        let uploadedResourceIds = [];
        let failedFileNames = [];
        let invalidFileNames = [];

        //File Limit Check
        if (files.length > maxFiles) {
            const title = window.locale?.t('upload_limit_title') || "Upload Limit";
            const message = window.locale?.t('upload_limit_message', { maxFiles }) || `You may only upload up to ${maxFiles} files at a time.`;
            modalService.show(title, message);
            return;
        }

        //File Type Check
        files.forEach(file => {
            if (!this.isValidFileType(file)) {
                invalidFileNames.push(file.name);
            }
        })
        if (invalidFileNames.length) {
            this.showInvalidFilesModal(invalidFileNames);
            return;
        }

        // Show pre-upload modal to collect metadata before uploading
        const preUploadResult = await window.preUploadFilesModal.show(files);
        if (!preUploadResult.confirmed) {
            return;
        }
        const fileMetadata = preUploadResult.metadata || {};
        const effectiveCollectionId = preUploadResult.collectionId || collectionId;

        let completedCount = 0;
        const tasks = files.map(file => {
            const metadata = fileMetadata[file.name] || null;
            return this.uploadFile(file, null, effectiveCollectionId, metadata, true)
                .then((result) => {
                    uploadedFileNames.push(file.name);
                    uploadedResourceIds.push(result.resourceId);
                })
                .catch(() => {
                    failedFileNames.push(file.name);
                })
                .finally(() => {
                    completedCount++;
                    window.uploadFilesModal.updateLoadingMessage(completedCount, files.length);
                });
        });

        window.uploadFilesModal.show();
        window.uploadFilesModal.updateLoadingMessage(0, files.length);
        await Promise.all(tasks);

        //Add a 1-second delay before fetching jobs
        await new Promise(resolve => setTimeout(resolve, 1000));

        window.uploadFilesModal.showCompleteMessage(completeMessage, uploadedFileNames, uploadedResourceIds, failedFileNames);

        // Refresh quota after successful uploads
        if (uploadedFileNames.length > 0) {
            await this.fetchAndUpdateQuota();
        }

        return;

    },

    async delayWithBlockUI(delay = 1000) {
        store.dispatch({ type: 'incrementRequests' });

        await new Promise(resolve => setTimeout(resolve, delay));

        store.dispatch({ type: 'decrementRequests' });
    },

    showInvalidFilesModal(invalidFileNames) {
        window.invalidFilesModal?.show(invalidFileNames)
    },

    showQuotaLimitModal() {
        window.quotaLimitModal?.show();
    },

    async fetchJobs() {
        // Lazy import only when needed
        const { jobService } = await import('../api/jobService');
        const store = await getStore();
        
        try {
            const filter = {
                status: {
                    $ne: 'completed'
                },
                jobType: {
                    $eq: 'ResourceUpload'
                }
            };

            const jobs = await jobService.get(1, 10, filter);
            
            // Add null check to prevent "Cannot read properties of undefined" error
            if (jobs && jobs.data && Array.isArray(jobs.data)) {
                jobs.data.forEach((job, i) => {
                    if (typeof(job.parameters) === "string") {
                        jobs.data[i].parameters = JSON.parse(job.parameters);
                    }
                })
                store.dispatch({ type: 'setJobs', payload: jobs });
            } else {
                console.warn('fetchJobs: Invalid response structure', jobs);
                // Dispatch empty result to prevent errors
                store.dispatch({ type: 'setJobs', payload: { data: [], pagination: {} } });
            }
        } catch (error) {
            console.error('fetchJobs: Failed to fetch jobs', error);
            // Dispatch empty result to prevent errors
            store.dispatch({ type: 'setJobs', payload: { data: [], pagination: {} } });
        }
    },
    
    async fetchResources() {
        // Lazy import only when needed
        const { resourceService } = await import('../api/resourceService');
        const store = await getStore();
        
        try {
            const resources = await resourceService.get();
            
            // Add null check to prevent errors
            if (resources && resources.data && Array.isArray(resources.data)) {
                store.dispatch({type: 'setResources', payload: resources})
            } else {
                console.warn('fetchResources: Invalid response structure', resources);
                store.dispatch({type: 'setResources', payload: { data: [], pagination: {} }})
            }
        } catch (error) {
            console.error('fetchResources: Failed to fetch resources', error);
            store.dispatch({type: 'setResources', payload: { data: [], pagination: {} }})
        }
    },

    async downloadResource(resource) {
        // Lazy import only when needed
        const { resourceService } = await import('../api/resourceService');
        
        const data = await resourceService.resourceDownload(resource.resourceId);
        this.downloadFile(data.downloadUrl, '');
    },

    async downloadExtractJson(resource) {
        // Lazy import only when needed
        const { resourceExtractService } = await import('../api/resourceExtractService');
        
        const data = await resourceExtractService.json(resource.resourceId);
        const parsed = JSON.parse(data.content);
        const formatted = JSON.stringify(parsed, null, 2);
        const blob = new Blob([formatted], { type: 'application/json' });
        const filename = (resource.displayName || 'extract' ) + '.json';
        this.downloadBlob(blob, filename);
    },

    async downloadExtractText(resource) {
        // Lazy import only when needed
        const { resourceExtractService } = await import('../api/resourceExtractService');
        
        const data = await resourceExtractService.txt(resource.resourceId);
        const blob = new Blob([data], { type: 'text/plain' });
        const filename = (resource.displayName || 'extract' ) + '.txt';
        this.downloadBlob(blob, filename);
    },

    async downloadExtractMarkdown(resource) {
        // Lazy import only when needed
        const { resourceExtractService } = await import('../api/resourceExtractService');
        
        const data = await resourceExtractService.md(resource.resourceId);
        const blob = new Blob([data], { type: 'text/plain' });
        const filename = (resource.displayName || 'extract' ) + '.md';
        this.downloadBlob(blob, filename);
    },

    _deliverCsv(data, filename, preview = false) {
        if (preview && typeof window !== 'undefined' && window.csvPreviewModal) {
            window.csvPreviewModal.show(data, filename);
            return;
        }
        const blob = new Blob([data], { type: "text/csv" });
        this.downloadBlob(blob, filename);
    },

    async exportSingleResourceCsv(resource, includeMetadata = [], preview = false) {
        // Lazy import only when needed
        const { resourceService } = await import('../api/resourceService');

        const formatDisplayName = (name) => {
            return name
                .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')    // Remove invalid characters
                .replace(/\.+$/, '')                      // Remove trailing periods
                .replace(/\s+$/, '')                      // Remove trailing spaces
                .split(/\s+/)                             // Split by whitespace
                .slice(0, 3)                              // First 3 words
                .map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                )
                .join('');
        }

        const filter = {
            resourceId: {
                $eq: resource.resourceId
            }
        }

        const type = this.mediaTypeDisplay(resource.mediaType);
        const name = formatDisplayName(resource.displayName);
        const temp_filename = `${name}_${type}_metadata.csv`;

        await this.exportResourceCsv(filter, temp_filename, "", false, includeMetadata, preview);
    },

    async exportResourceCsv(filter, temp_filename, query = "", semantic = false, includeMetadata = [], preview = false) {
        // Lazy import only when needed
        const { resourceService } = await import('../api/resourceService');

        const data = await resourceService.exportCsv(filter, query, "", semantic, includeMetadata);

        const now = new Date();
        const dateString = now.toISOString().split("T")[0];
        const filename = temp_filename ? temp_filename : `bulk_export_${dateString}.csv`;

        this._deliverCsv(data, filename, preview);
    },

    async exportSkillsCsv(filter = null, includeResources = false, preview = false) {
        // Lazy import only when needed
        const { skillService } = await import('../api/skillService');

        const data = await skillService.exportCsv(filter, includeResources);

        const now = new Date();
        const dateString = now.toISOString().split("T")[0];
        const filename = `skills_export_${dateString}.csv`;

        this._deliverCsv(data, filename, preview);
    },

    async exportTagsCsv(filter = null, includeResources = false, preview = false) {
        // Lazy import only when needed
        const { tagService } = await import('../api/tagService');

        const data = await tagService.exportCsv(filter, includeResources);

        const now = new Date();
        const dateString = now.toISOString().split("T")[0];
        const filename = `tags_export_${dateString}.csv`;

        this._deliverCsv(data, filename, preview);
    },

    async exportCollectionCsv(filter = null, includeResources = false, preview = false) {
        // Lazy import only when needed
        const { collectionService } = await import('../api/collectionService');

        const data = await collectionService.exportCsv(filter, includeResources);

        const now = new Date();
        const dateString = now.toISOString().split("T")[0];
        const filename = `collections_export_${dateString}.csv`;

        this._deliverCsv(data, filename, preview);
    },

    async exportAttributesCsv(filter = null, includeResources = false, preview = false) {
        // Lazy import only when needed
        const { attributeService } = await import('../api/attributeService');

        const data = await attributeService.exportCsv(filter, includeResources);

        const now = new Date();
        const dateString = now.toISOString().split("T")[0];
        const filename = `attributes_export_${dateString}.csv`;

        this._deliverCsv(data, filename, preview);
    },

    async exportKeywordsCsv(preview = false) {
        // Lazy import only when needed
        const { keywordService } = await import('../api/keywordService');

        const data = await keywordService.exportCsv();

        const now = new Date();
        const dateString = now.toISOString().split("T")[0];
        const filename = `keywords_export_${dateString}.csv`;

        this._deliverCsv(data, filename, preview);
    },

    async downloadKeyFile(resourceIds = [], filters = null, query = null, semantic = false) {
        try {
            // Lazy import only when needed
            const { resourceService } = await import('../api/resourceService');

            // Build filter object based on what's provided
            let filter = null;

            if (resourceIds && resourceIds.length) {
                // If specific resource IDs are provided, construct filter with $in operator
                filter = {
                    resourceId: {
                        $in: resourceIds
                    }
                };
            } else if (filters) {
                // Otherwise use the provided filters object
                filter = filters;
            }

            // Call the key file generation endpoint
            const data = await resourceService.generateKeyFile(filter, query, semantic);

            // Create blob and download
            const blob = new Blob([data], { type: 'text/csv' });

            // Generate filename with timestamp
            const now = new Date();
            const dateString = now.toISOString().split("T")[0];
            const filename = `keyfile_${dateString}.csv`;

            this.downloadBlob(blob, filename);
        } catch (error) {
            console.error('Failed to generate key file:', error);

            // Show error modal to user
            const { modalService } = await import('./modalService');
            const title = window.locale?.t('keyfile_generation_failed') || 'Key File Generation Failed';
            const defaultMessage = window.locale?.t('keyfile_generation_error') || 'An error occurred while generating the key file.';
            const message = error?.response?.data?.message || error?.message || defaultMessage;
            modalService.show(title, message);

            throw error;
        }
    },

    async launchPlayer(resource, chunk, searchterm) {
        const sliceId = await this.getSliceId();
        let launchUrl = `/content/${sliceId}/${resource.resourceId}/${resource.launchFile}`;

        const params = new URLSearchParams({
            id: resource.resourceId,
            type: resource.mediaType,
            title: resource.displayName,
            launchUrl: launchUrl,
            nocache: this.generateUUID()
        });

        if (chunk && chunk.bookmark) {
            params.append('bookmark', this.encodeObjectToBase64(chunk.bookmark));
        }

        if (searchterm) {
            params.append('text', searchterm);
        }

        if (chunk && chunk.text) {
            params.append('paragraphStart', chunk.text.substring(0, 50));
        }

        const url = `/player/?${params.toString()}`;

        // Calculate 75% of available screen size, capped at 1200x750
        const width = Math.min(1200, Math.floor(window.screen.availWidth * 0.75));
        const height = Math.min(750, Math.floor(window.screen.availHeight * 0.75));

        // Position near top-left (e.g., 5% from left, 5% from top)
        const left = Math.floor(window.screenX + window.screen.availWidth * 0.05);
        const top = Math.floor(window.screenY + window.screen.availHeight * 0.05);

        const features = `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no`;
        const windowName = (resource.displayName || 'Content Player');

        // Inside an impersonation / workspace-access session, the
        // request to /content/... lands at Lambda@Edge which can only
        // validate JWT-shape session tokens — it has no way to look up
        // the imp_/wsa_ token in Redux. The session JWT lives in the
        // launch_token cookie; we mint it on demand here so it only
        // exists when a preview is actually being launched (and
        // doesn't leave a slice-scoped credential sitting in the
        // browser when the admin is doing other impersonation work).
        //
        // window.open MUST be called synchronously inside this handler
        // — once we await, browsers strip the user-gesture context and
        // popup blockers fire. So open with about:blank first, fetch
        // the cookie, then redirect the popup to the actual player URL.
        const store = await getStore();
        const impersonationToken = store.getState().impersonation?.token;
        const insideAccessSession = typeof impersonationToken === 'string'
            && (impersonationToken.startsWith('imp_') || impersonationToken.startsWith('wsa_'));

        if (insideAccessSession) {
            const popup = window.open('about:blank', windowName, features);
            if (!popup) return; // popup blocker; nothing more we can do here
            try {
                const { impersonationService } = await import('../api/impersonationService.js');
                await impersonationService.refreshLaunchTokenCookie({ skipLoading: true });
            } catch (err) {
                // Non-fatal — try the navigation anyway. If the cookie
                // wasn't refreshed and the session JWT is missing, the
                // player will redirect to the auth-required page; that's
                // the same failure mode the user would have seen before.
                console.warn('[tools.launchPlayer] launch_token refresh failed:', err);
            }
            popup.location = url;
            return;
        }

        window.open(url, windowName, features);
    },

    formatSeconds(totalSeconds = 0) {
        const s = parseInt(totalSeconds);
        const hours = Math.floor(s / 3600);
        const minutes = Math.floor((s % 3600) / 60);
        const seconds = s % 60;

        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },

    isAttributeReasonRequired(key) {
        return (key === 'status' || key === 'action');
    },

    async updateResourceAttribute(resource, key, value) {
        const { attributeService } = await import('../api/attributeService');

        let reason = null;

        if (this.isAttributeReasonRequired(key)) {
            reason = await window.attributeReasonModal.show(key, value);

            if (reason === null) {
                const error = new Error('User cancelled attribute change');
                error.code = 'USER_CANCELLED';
                throw error;
            }
        }

        const existingAttr = resource.attributes?.find(attr => attr.key === key);

        if (existingAttr) {
            await attributeService.put(resource.resourceId, key, value, reason);
        } else {
            await attributeService.post(resource.resourceId, key, value, reason);
        }
    },

    async bulkUpdateResourceAttribute(resources, key, value) {
        const { attributeService } = await import('../api/attributeService');

        let reason = null;

        if (this.isAttributeReasonRequired(key)) {
            reason = await window.attributeReasonModal.show(key, value);

            if (reason === null) {
                const error = new Error('User cancelled attribute change');
                error.code = 'USER_CANCELLED';
                throw error;
            }
        }

        const tasks = resources.map(resource => {
            const existingAttr = resource.attributes?.find(attr => attr.key === key);

            if (existingAttr) {
                return attributeService.put(resource.resourceId, key, value, reason);
            } else {
                return attributeService.post(resource.resourceId, key, value, reason);
            }
        });

        await Promise.all(tasks);
    },

    getProviderName(provider) {
        const providerMap = {
            'google_drive': 'provider_googledrive',
            'sharepoint': 'provider_sharepoint',
            'dropbox': 'provider_dropbox',
            's3': 'provider_amazons3',
            'box': 'provider_box'
        };
        const key = providerMap[provider];
        return key ? (window.locale?.t(key) || provider) : provider;
    },

    getProviderIcon(provider) {
        const map = {
            'google_drive': '📁',
            'sharepoint': '📊',
            'dropbox': '📦',
            's3': '☁️',
            'box': '📂'
        };
        return map[provider] || '📁';
    },

    getProviderLogoPath(provider) {
        const map = {
            'google_drive': '/providers/google-drive.svg',
            'sharepoint': '/providers/sharepoint.svg',
            'dropbox': '/providers/dropbox.svg',
            's3': '/providers/s3.svg',
            'box': '/providers/box.svg'
        };
        return map[provider] || '';
    },

    getStatusBadgeClass(status) {
        const map = {
            'connected': 'badge-success',
            'disconnected': 'badge-ghost',
            'error': 'badge-error'
        };
        return map[status] || 'badge-ghost';
    },


    /**
     * Get current user context (from Redux store or API)
     * Uses Redux as single source of truth to avoid duplicate API calls
     * @returns {Promise<Object>} User context with roles and permissions
     */
    async getUserContext() {
        const store = await getStore();
        
        // Check Redux store first (single source of truth)
        const reduxContext = store.getState().userContext;
        if (reduxContext) {
            return reduxContext;
        }

        // If we're on a public page (like login after logout), don't attempt /auth/me
        // Return guest context immediately to prevent 401 triggering session-expired flow
        const currentHash = window.location.hash;
        const isIntentionalLogout = sessionStorage.getItem('intentional_logout') === 'true';
        
        if (isPublicRoute(currentHash) || isIntentionalLogout) {
            console.log('[Tools] On public page or intentional logout, returning guest context without API call');
            return {
                userId: null,
                email: null,
                role: 'TENANT_USER',
                permissions: [],
                sliceId: null,
                customerId: null
            };
        }

        // If a request is already in progress, wait for it and return the same promise
        if (userContextPromise) {
            return userContextPromise;
        }

        // Create and store the promise BEFORE starting the async operation
        // This prevents race conditions where multiple callers think no request is in progress
        userContextPromise = (async () => {
            try {
                const authService = await getAuthService();

                // Background bootstrap fetch — never user-triggered, so we
                // suppress the global spinner. Page skeletons cover the
                // brief gap before Redux is populated.
                const result = await authService.getCurrentUser({ skipLoading: true });

                // AuthService.getCurrentUser returns { success, user, error }
                // We need to extract the user object
                if (!result.success || !result.user) {
                    throw new Error(result.error || 'Failed to get user context');
                }

                const context = result.user;
                // Store in Redux (single source of truth for all components)
                store.dispatch({ type: 'setUserContext', payload: context });
                userContextPromise = null;
                return context;
            } catch (error) {
                console.error('Failed to fetch user context:', error);
                userContextPromise = null;
                
                // Check if this is a 401 Unauthorized error
                if (error.response && error.response.status === 401) {
                    // Check if we're already on the login page
                    const currentHash = window.location.hash;
                    if (!currentHash.includes('login')) {
                        console.log('User not authenticated, redirecting to login');
                        // Clear any stale auth state
                        this.clearUserContext();
                        localStorage.removeItem('authToken');
                        // Redirect to login page (will reload the page)
                        window.location.href = '/#login';
                    }
                    // Return guest context (on login page or while redirecting)
                    return {
                        userId: null,
                        email: null,
                        role: 'TENANT_USER',
                        permissions: [],
                        sliceId: null,
                        customerId: null
                    };
                }
                
                // For other errors, return a default context with most restrictive role
                return {
                    userId: null,
                    email: null,
                    role: 'TENANT_USER',
                    permissions: [],
                    sliceId: null,
                    customerId: null
                };
            }
        })();

        return userContextPromise;
    },

    /**
     * Clear user context from Redux store (call after logout)
     */
    async clearUserContext() {
        const store = await getStore();
        store.dispatch({ type: 'clearUserContext' });
        userContextPromise = null;
    },

    /**
     * Check if current user has a specific role
     * @param {string} role - Role to check (e.g., 'SYSTEM_ADMIN')
     * @returns {Promise<boolean>}
     */
    async hasRole(role) {
        const context = await this.getUserContext();
        return context.role === role;
    },

    /**
     * Check if current user has any of the specified roles
     * @param {string[]} roles - Array of roles to check
     * @returns {Promise<boolean>}
     */
    async hasAnyRole(roles) {
        const context = await this.getUserContext();
        return roles.includes(context.role);
    },

    /**
     * Check if current user is an admin (SYSTEM_ADMIN, CUSTOMER_ADMIN, or TENANT_ADMIN)
     * @returns {Promise<boolean>}
     */
    async isAdmin() {
        return this.hasAnyRole(['SYSTEM_ADMIN', 'CUSTOMER_ADMIN', 'TENANT_ADMIN']);
    },

    /**
     * Check if current user has a specific permission
     * @param {string} permission - Permission to check (e.g., 'users:write')
     * @returns {Promise<boolean>}
     */
    async hasPermission(permission) {
        const context = await this.getUserContext();
        return context.permissions && context.permissions.includes(permission);
    },

    /**
     * Get current cache buster value from Redux store
     * Used to append to image URLs and other assets that need cache invalidation
     * @returns {Promise<number>} Timestamp value to use as cache buster
     */
    async getCacheBuster() {
        const store = await getStore();
        return store.getState().cacheBuster;
    },

    /**
     * Refresh the cache buster value
     * Forces a hard refresh of all assets using the cache buster
     */
    async refreshCacheBuster() {
        const store = await getStore();
        store.dispatch({ type: 'refreshCacheBuster' });
    },

    /**
     * Append cache buster parameter to a URL
     * Synchronously adds the current cache buster value as a query parameter
     * @param {string} url - URL to append cache buster to
     * @returns {string} URL with cache buster parameter
     * @example
     * tools.appendCacheBusterParam('https://example.com/logo.png')
     * // Returns: 'https://example.com/logo.png?cb=1234567890'
     */
    appendCacheBusterParam(url) {
        if (!url) return url;

        // Access store synchronously via window.store
        if (!window.store) {
            console.warn('[Tools] Store not available yet, returning URL without cache buster');
            return url;
        }

        const cacheBuster = window.store.getState().cacheBuster;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}cb=${cacheBuster}`;
    },

    /**
     * Poll for customer logo processing completion
     * Repeatedly checks logoMetadata status until completed, failed, or timeout
     * @param {string} customerId - Customer ID to check logo status for
     * @param {number} maxAttempts - Maximum number of polling attempts (default: 30)
     * @param {number} intervalMs - Time between polling attempts in ms (default: 2000)
     * @returns {Promise<Object>} Updated customer data when logo processing completes
     * @throws {Error} If logo processing fails or times out
     * @example
     * const updatedCustomer = await tools.pollLogoCompletion('customer-123')
     */
    async pollLogoCompletion(customerId, maxAttempts = 30, intervalMs = 2000) {
        // Lazy import only when needed
        const { customerService } = await import('../api/customerService');

        let attempts = 0;

        const checkStatus = async () => {
            try {
                const customerData = await customerService.getById(customerId, '', true);

                // Check if logoMetadata indicates completion
                let logoMetadata;
                try {
                    logoMetadata = customerData.logoMetadata ? JSON.parse(customerData.logoMetadata) : null;
                } catch (e) {
                    console.warn('[Tools] Failed to parse logoMetadata', e);
                }

                if (logoMetadata?.status === 'completed' && customerData.logoUrl) {
                    console.log('[Tools] Logo processing completed successfully');
                    return customerData;
                }

                if (logoMetadata?.status === 'failed') {
                    console.error('[Tools] Logo processing failed:', logoMetadata.error);
                    throw new Error(logoMetadata.error || 'Logo processing failed');
                }

                // Still processing, continue polling
                attempts++;
                if (attempts < maxAttempts) {
                    return new Promise((resolve) => {
                        setTimeout(() => resolve(checkStatus()), intervalMs);
                    });
                } else {
                    console.warn('[Tools] Logo processing timed out');
                    throw new Error('Logo processing timed out');
                }
            } catch (error) {
                console.error('[Tools] Error polling logo status:', error);
                throw error;
            }
        };

        return checkStatus();
    },

    /**
     * Save page state to sessionStorage with timestamp
     * @param {string} key - Storage key to save under
     * @param {Object} stateObject - State object to save (timestamp will be added automatically)
     * @returns {boolean} True if saved successfully, false otherwise
     */
    savePageState(key, stateObject) {
        try {
            const stateWithTimestamp = {
                ...stateObject,
                timestamp: Date.now()
            };
            sessionStorage.setItem(key, JSON.stringify(stateWithTimestamp));
            return true;
        } catch (error) {
            console.warn(`[Tools] Failed to save page state for ${key}:`, error);
            return false;
        }
    },

    /**
     * Load page state from sessionStorage with staleness check
     * Automatically clears the state after reading (one-time restoration)
     * Automatically clears navigation flags after reading state
     * @param {string} key - Storage key to load from
     * @param {number} maxAgeMs - Maximum age in milliseconds (default: 1 hour)
     * @returns {Object|null} Saved state object (without timestamp) or null if not found/stale
     */
    loadPageState(key, maxAgeMs = 60 * 60 * 1000) {
        try {
            const saved = sessionStorage.getItem(key);
            if (!saved) return null;

            const state = JSON.parse(saved);

            // Check if state is stale
            if (Date.now() - state.timestamp > maxAgeMs) {
                // Clear this stale state
                sessionStorage.removeItem(key);

                // Also clear navigation flags when state is stale
                // to prevent restored browser sessions from triggering state restoration
                this.clearBackNavigationFlags();

                return null;
            }

            // Clear the state now that we've read it (one-time restoration)
            sessionStorage.removeItem(key);

            // Also clear navigation flags after successfully reading state
            // This prevents the next normal navigation from incorrectly thinking it's a back navigation
            this.clearBackNavigationFlags();

            // Return state without timestamp property
            const { timestamp, ...stateWithoutTimestamp } = state;
            return stateWithoutTimestamp;
        } catch (error) {
            console.warn(`[Tools] Failed to load page state for ${key}:`, error);
            return null;
        }
    },

    /**
     * Clear one or more page state keys from sessionStorage
     * @param {...string} keys - Storage keys to clear
     * @returns {boolean} True if all cleared successfully, false otherwise
     */
    clearPageState(...keys) {
        try {
            keys.forEach(key => {
                sessionStorage.removeItem(key);
            });
            return true;
        } catch (error) {
            console.warn('[Tools] Failed to clear page state:', error);
            return false;
        }
    }
};

window.tools = tools;
